/**
 * DirectorySourcesContent — Account > People > Directory Sources subtab
 * (ADR-141 §8 / P7).
 *
 * Table of configured directory sources (provider, status, last full /
 * delta sync) with an add/edit drawer (`DirectorySourceDrawer`),
 * per-row sync-now, delete, and an expandable sync-log panel (last 50
 * runs: type, timing, per-kind counts, success/errors).
 *
 * Visibility is gated by the parent (`AuthorizationContent`): feature
 * flag + `contacts.manage_contactdirectorysource` — the viewset denies
 * the whole collection without that perm, so there is no read-only
 * variant of this surface.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Center,
  Flex,
  IconButton,
  Spinner,
  Table,
  Text,
} from '@chakra-ui/react';
import { HiXMark } from 'react-icons/hi2';
import {
  DataTable,
  RowActionsMenu,
  StatusBadge,
  TableOptions,
  type Column,
  type StatusType,
} from '@themes/aegix';
import { toaster } from '../../ui/toaster';
import {
  directoryProviderLabel,
  type ContactDirectorySource,
  type ContactDirectorySourceStatus,
} from '../../../services/contacts.service';
import {
  useDeleteDirectorySource,
  useDirectorySources,
  useDirectorySourceSyncLog,
  useSyncDirectorySourceNow,
} from '../../../hooks/queries/useDirectorySources';
import { DirectorySourceDrawer } from './DirectorySourceDrawer';

const STATUS_BADGE: Record<ContactDirectorySourceStatus, StatusType> = {
  active: 'active',
  paused: 'inactive',
  error: 'error',
};

function formatSyncTime(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : 'Never';
}

const columns: Column<ContactDirectorySource>[] = [
  {
    key: 'provider_key',
    label: 'Provider',
    width: '220px',
    render: (row) => (
      <Text color="text.primary" data-e2e="sourceProvider">
        {directoryProviderLabel(row.provider_key)}
      </Text>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    width: '110px',
    render: (row) => (
      <StatusBadge status={STATUS_BADGE[row.status ?? 'active']} />
    ),
  },
  {
    key: 'last_full_sync',
    label: 'Last full sync',
    render: (row) => (
      <Text color="text.secondary" data-e2e="sourceLastFullSync">
        {formatSyncTime(row.last_full_sync)}
      </Text>
    ),
  },
  {
    key: 'last_delta_sync',
    label: 'Last delta sync',
    render: (row) => (
      <Text color="text.secondary" data-e2e="sourceLastDeltaSync">
        {formatSyncTime(row.last_delta_sync)}
      </Text>
    ),
  },
];

export function DirectorySourcesContent() {
  const { t } = useTranslation();
  const { data: sources = [], isLoading, error } = useDirectorySources();
  const deleteMutation = useDeleteDirectorySource();
  const syncMutation = useSyncDirectorySourceNow();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingSource, setEditingSource] =
    useState<ContactDirectorySource | null>(null);

  // Source whose sync-log panel is expanded underneath the table. Holds the
  // whole row, not just its id: the panel header renders
  // `directoryProviderLabel(logSource.provider_key)`.
  const [logSource, setLogSource] = useState<ContactDirectorySource | null>(
    null,
  );

  const syncLogQuery = useDirectorySourceSyncLog(logSource?.id ?? null);

  const openAddDrawer = () => {
    setEditingSource(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (src: ContactDirectorySource) => {
    setEditingSource(src);
    setDrawerOpen(true);
  };

  const handleDelete = async (src: ContactDirectorySource) => {
    try {
      await deleteMutation.mutateAsync(src.id);
      if (logSource?.id === src.id) setLogSource(null);
      toaster.create({
        title: 'Directory source removed',
        type: 'success',
        duration: 3000,
      });
    } catch (err) {
      toaster.create({
        title: 'Delete failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        type: 'error',
      });
    }
  };

  const handleSyncNow = async (src: ContactDirectorySource) => {
    try {
      await syncMutation.mutateAsync(src.id);
      toaster.create({
        title: 'Sync triggered',
        description: `Running sync for ${directoryProviderLabel(src.provider_key)}...`,
        type: 'success',
        duration: 3000,
      });
    } catch (err) {
      toaster.create({
        title: 'Sync failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        type: 'error',
      });
    }
  };

  return (
    <>
      <TableOptions
        primaryAction={{
          label: t('contacts.addSource', 'Add Source'),
          onClick: openAddDrawer,
          'data-e2e': 'addSource',
        }}
      />

      <Box px={5} pb={5}>
        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : error ? (
          <Center py={12}>
            <Text color="text.danger">
              {error instanceof Error
                ? error.message
                : 'Failed to load directory sources'}
            </Text>
          </Center>
        ) : sources.length === 0 ? (
          <Center py={12}>
            <Text color="text.secondary" data-e2e="sourcesEmptyState">
              No directory sources configured
            </Text>
          </Center>
        ) : (
          <DataTable
            columns={columns}
            data={sources}
            keyExtractor={(row) => String(row.id)}
            onRowClick={openEditDrawer}
            getRowProps={() => ({ 'data-e2e': 'sourceRow' })}
            renderRowActions={(row) => (
              <RowActionsMenu
                actions={[
                  {
                    label: 'Edit',
                    onClick: () => openEditDrawer(row),
                    e2eTitle: 'editSource',
                  },
                  {
                    label: 'Sync now',
                    onClick: () => void handleSyncNow(row),
                    e2eTitle: 'syncNow',
                  },
                  {
                    label: 'View sync log',
                    onClick: () => setLogSource(row),
                    e2eTitle: 'viewSyncLog',
                  },
                  {
                    label: 'Delete',
                    variant: 'danger',
                    onClick: () => void handleDelete(row),
                    e2eTitle: 'deleteSource',
                  },
                ]}
              />
            )}
            caption="Directory sources"
          />
        )}

        {logSource && (
          <Box
            mt={4}
            borderWidth="1px"
            borderColor="border.tertiary"
            borderRadius="md"
            p={3}
            data-e2e="syncLogPanel"
          >
            <Flex justify="space-between" align="center" mb={2}>
              <Text fontSize="md" fontWeight={600} color="text.primary">
                Sync log — {directoryProviderLabel(logSource.provider_key)}
              </Text>
              <IconButton
                aria-label="Close sync log"
                size="xs"
                variant="ghost"
                onClick={() => setLogSource(null)}
                data-e2e="closeSyncLog"
              >
                <HiXMark />
              </IconButton>
            </Flex>
            {syncLogQuery.isLoading ? (
              <Center py={6}>
                <Spinner size="md" color="button.primary" />
              </Center>
            ) : (syncLogQuery.data ?? []).length === 0 ? (
              <Text
                fontSize="sm"
                color="text.secondary"
                data-e2e="syncLogEmpty"
              >
                No syncs have run yet
              </Text>
            ) : (
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Type</Table.ColumnHeader>
                    <Table.ColumnHeader>Started</Table.ColumnHeader>
                    <Table.ColumnHeader>Finished</Table.ColumnHeader>
                    <Table.ColumnHeader>Outcome</Table.ColumnHeader>
                    <Table.ColumnHeader>Created</Table.ColumnHeader>
                    <Table.ColumnHeader>Updated</Table.ColumnHeader>
                    <Table.ColumnHeader>Deactivated</Table.ColumnHeader>
                    <Table.ColumnHeader>Tags</Table.ColumnHeader>
                    <Table.ColumnHeader>Errors</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {(syncLogQuery.data ?? []).map((log) => (
                    <Table.Row key={log.id} data-e2e="syncLogRow">
                      <Table.Cell>
                        {log.sync_type === 'full' ? 'Full' : 'Delta'}
                      </Table.Cell>
                      <Table.Cell>{formatSyncTime(log.started_at)}</Table.Cell>
                      <Table.Cell>{formatSyncTime(log.finished_at)}</Table.Cell>
                      <Table.Cell>
                        {log.success == null ? (
                          <StatusBadge status="processing" />
                        ) : (
                          <StatusBadge
                            status={log.success ? 'success' : 'failed'}
                          />
                        )}
                      </Table.Cell>
                      <Table.Cell>{log.contacts_created}</Table.Cell>
                      <Table.Cell>{log.contacts_updated}</Table.Cell>
                      <Table.Cell>{log.contacts_deactivated}</Table.Cell>
                      <Table.Cell>{log.tags_assigned}</Table.Cell>
                      <Table.Cell>
                        <Text color="red.700" fontSize="xs">
                          {Array.isArray(log.errors)
                            ? (log.errors as string[]).join('; ')
                            : ''}
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            )}
          </Box>
        )}
      </Box>

      <DirectorySourceDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        source={editingSource}
      />
    </>
  );
}
